<template>
  <tippy
    ref="tippyRef"
    :trigger="props.disabled ? 'manual' : 'click'"
    theme="light paddingless"
    placement="bottom-start"
    :arrow="false"
    interactive
    :hide-on-click="false"
    @keydown.esc="hidePopover"
    @hide="popoverHide"
  >
    <template #trigger>
      <global-events @keydown.esc="hidePopover" />
      <filter-trigger
        :data-testid="`filter-item-${filterId}`"
        :tippy-shown="tippyShown"
        :description="description"
        :active-filter="activeFilter"
        :disabled="disabled"
        :intelligence-icon="intelligenceIcon"
        :bot-icon="botIcon"
        :filter-label="filterLabel"
        :selected-condition="selectedCondition"
        :emoji-src="emojiSrc"
        :value="value"
        :value-type="valueType"
        :force-show-remove-button="forceShowRemoveButton"
        :force-hide-remove-button="forceHideRemoveButton"
        :show-add-similar-button="showAddSimilarButton"
        :error="error"
        :suffix="suffix"
        @add="addingSimilarFilter = true"
        @remove="$emit('remove')"
      />
    </template>

    <div
      ref="filterItemPopup"
      class="min-w-[320px] p-4"
      :data-id="`filter-item-popup-${filterLabel.toLocaleLowerCase().replace(' ', '_')}`"
    >
      <div class="flex justify-between gap-2">
        <div class="text-base font-medium text-gray-80">{{ filterLabel }}</div>
        <ui-tag
          v-if="status === FilterItemStatus.INACTIVE"
          v-tippy="{
            content: isSpotlight ? $t('filter.tag.inactive_spotlight.tooltip') : $t('filter.tag.inactive.tooltip'),
          }"
          class="mt-1"
        >
          {{ $t('filter.tag.inactive.label') }}
        </ui-tag>
      </div>
      <i18n-t keypath="filter.option.concatenated" tag="div">
        <template #description>
          <div v-if="description" class="text-small font-small mt-2 text-gray-60">{{ description }}</div>
        </template>
        <template #condition>
          <ui-dropdown
            v-if="conditions?.length > 1"
            :mode="Modes.Single"
            :model-value="newSelectedCondition ? [newSelectedCondition] : []"
            close-on-select
            :max-width="999"
            :options="conditions"
            :search-placeholder="$t('universal.search')"
            :no-results-text="$t('universal.no_items_found')"
            filter-results
            size="lg"
            :is-object="true"
            prop-to-check="name"
            class="group mt-5 w-full outline-0"
            data-ignore-theme
            append-to-document
            label="label"
            @update:model-value="newSelectedCondition = $event"
          >
            <template #trigger="{ active }">
              <ui-menu-simple-trigger v-if="newSelectedCondition?.valueRequired" class="[&>svg]:ml-1">
                {{ newSelectedCondition?.label || $t('conversations.filters.form.condition_placeholder') }}
              </ui-menu-simple-trigger>
              <ui-menu-select-trigger v-else small :active>
                <template v-if="newSelectedCondition?.label">
                  {{ newSelectedCondition.label }}
                </template>
                <span v-else class="text-gray-50">{{ $t('conversations.filters.form.condition_placeholder') }}</span>
              </ui-menu-select-trigger>
            </template>
          </ui-dropdown>
          <span v-else class="group mt-5 flex w-full items-center text-gray-70 outline-0 hover:text-gray-90">
            {{ newSelectedCondition?.label || $t('conversations.filters.form.condition_placeholder') }}
          </span>
        </template>
        <template #selectedOption>
          <ui-form-field
            v-if="newSelectedCondition?.valueRequired"
            class="mt-2 flex flex-col"
            :error="
              applyingDisabled && applyClicked
                ? $t('filter.add', { filterName: props.filterLabel.toLocaleLowerCase() })
                : undefined
            "
          >
            <ui-input
              v-if="valueType === FilterValueType.Loading"
              disabled
              small
              :model-value="$t('conversations.filters.form.value_loading')"
            />
            <ui-input
              v-else-if="valueType === FilterValueType.NoValues"
              disabled
              small
              :model-value="$t('conversations.filters.form.value_empty')"
            />
            <tag-picker
              v-else-if="valueType === FilterValueType.SearchableTags"
              :model-value="newValue as DropdownSelectedValue[]"
              data-test="tag-search"
              :api-method="props.valueComponentProps?.apiMethod"
              class="bg-gray-10"
              open-direction="auto"
              @update:model-value="newValue = $event"
            />
            <custom-field-value-dropdown
              v-else-if="valueType === FilterValueType.CustomField"
              :field-external-id="filterId"
              :field-name="filterLabel"
              :model-value="newValue as DropdownSelectedValue[]"
              @update:model-value="newValue = Array.isArray($event) ? $event : [$event]"
            />
            <ui-dropdown
              v-if="valueType === FilterValueType.Generic"
              :mode="multipleValuesAllowed ? Modes.Multiple : Modes.Single"
              :groups="props.valueComponentProps.groups"
              :model-value="newValue as DropdownSelectedValue[]"
              :label="props.valueComponentProps.labelKey"
              :close-on-select="!multipleValuesAllowed"
              :max-width="999"
              size="lg"
              :select-all-mode="hasMultipleGroups ? SelectAllModes.None : SelectAllModes.All"
              :select-all-label="!hasMultipleGroups ? $t('filter.select_all') : undefined"
              allow-single-deselect
              :search-placeholder="$t('universal.search')"
              :no-results-text="props.valueComponentProps.noResultsText"
              :is-object="true"
              prop-to-check="value"
              has-search
              filter-results
              class="group outline-0"
              append-to-document
              :disabled="props.disabled"
              @update:model-value="newValue = Array.isArray($event) ? $event : [$event]"
            >
              <template #option-icon="{ option }">
                <users-icon v-if="option.type === GROUP" class="mr-1 h-4 w-4 text-gray-50" />
              </template>

              <template #option="{ option }">
                <div v-if="option?.value === 'separator'" class="-mx-4 my-2 h-[1px] flex-1 bg-gray-30" />
                <img
                  v-else-if="'emojiSrc' in option && option?.emojiSrc"
                  :src="option?.emojiSrc as string"
                  class="h-4 w-4"
                />
                <span v-else-if="option?.label" class="w-full truncate">{{ option.label }}</span>
              </template>

              <template #trigger="{ active }">
                <ui-menu-select-trigger small :active>
                  <img
                    v-if="getEmojiSrc(newValue as DropdownSelectedValue[])"
                    :src="getEmojiSrc(newValue as DropdownSelectedValue[])"
                    class="h-4 w-4"
                  />
                  <template v-else-if="getFieldLabel(newValue as DropdownSelectedValue[])">
                    {{ getFieldLabel(newValue as DropdownSelectedValue[]) }}
                  </template>
                  <span v-else class="text-gray-50">
                    {{
                      multipleValuesAllowed
                        ? $t('filter.multiple_value_placeholder')
                        : $t('filter.single_value_placeholder')
                    }}
                  </span>
                </ui-menu-select-trigger>
              </template>
            </ui-dropdown>
            <day-picker
              v-if="valueType === FilterValueType.Date"
              small
              :model-value="Array.isArray(newValue) ? undefined : (newValue as Date)"
              :initial-page-position="1"
              :max-date="new Date()"
              :min-date="new Date('2018-01-01')"
              @update:model-value="newValue = $event"
            />
            <ui-input
              v-else-if="valueType === FilterValueType.Text"
              :model-value="newValue as string"
              type="text"
              small
              @update:model-value="newValue = $event"
            />
            <multirow-text-input
              v-else-if="valueType === FilterValueType.MultirowText"
              :model-value="newValue as string[]"
              @update:model-value="newValue = $event"
            />
            <ui-input
              v-else-if="valueType === FilterValueType.Integer"
              v-model="newValue as string"
              type="number"
              min="0"
              small
              @update:model-value="(value) => validateNumber(Number(value))"
            />
          </ui-form-field>
        </template>
      </i18n-t>

      <ui-button small data-ignore-theme class="m-t-auto mt-4 w-full" @click="applyFilter">
        {{ $t('filter.apply') }}
      </ui-button>
      <div
        v-if="
          filterId === 'RATING_STATUS' &&
          newSelectedCondition?.name !== 'RATING_RATED' &&
          session.features.gradeTheReviewerDev
        "
        class="mt-3 flex items-start gap-2 text-xs font-normal text-gray-60"
      >
        <info-icon class="text-gray-60 icon-m" />
        {{ $t('filter.option.rating_status.info') }}
      </div>
    </div>
  </tippy>
</template>

<script lang="ts" setup>
import { InfoIcon, UsersIcon } from '@klausapp/ui-kit/icons'
import {
  UiButton,
  UiDropdownTypes,
  UiDropdownV2 as UiDropdown,
  UiInput,
  UiFormField,
  UiMenuSelectTrigger,
  UiMenuSimpleTrigger,
  UiTag,
  GlobalEvents,
} from '@klausapp/ui-kit'
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash-es'
import { onClickOutside } from '@vueuse/core'

import { Tippy } from '@/components/Tippy'
import DayPicker from '@/components/pickers/DayPicker.vue'
import TagPicker from '@/components/pickers/TagPicker.vue'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'

import { FilterItemStatus } from '@/modules/conversations/api/filters'
import {
  FilterValueType,
  type Condition,
  type ValueComponentProps,
  type FilterItemValue,
  type DropdownSelectedValue,
  type FilterItemEmit,
  GROUP,
} from './types'
import { isActiveFilter, valuesFilled } from './utils'
import CustomFieldValueDropdown from './CustomFieldValueDropdown.vue'
import MultirowTextInput from './MultirowTextInput.vue'
import FilterTrigger from './FilterTrigger.vue'

const props = defineProps<{
  filterId: string
  filterLabel: string
  description?: string
  conditions?: Condition[]
  selectedCondition: Condition
  value?: FilterItemValue
  valueType: FilterValueType
  valueComponentProps?: ValueComponentProps
  intelligenceIcon?: boolean
  botIcon?: boolean
  forceShowRemoveButton?: boolean
  forceHideRemoveButton?: boolean
  showAddSimilarButton?: boolean
  disabled?: boolean
  error?: boolean
  skipErrorCheck?: boolean
  suffix?: string
  status?: FilterItemStatus
  isSpotlight?: boolean
}>()

const emit = defineEmits<{
  (e: 'remove'): void
  (e: 'filter-applied', f: FilterItemEmit): void
  (e: 'add-similar-filter', f: FilterItemEmit): void
}>()

const { Modes, SelectAllModes } = UiDropdownTypes

const tippyRef = ref<InstanceType<typeof Tippy>>()
const filterItemPopup = ref<HTMLDivElement>()
const newSelectedCondition = ref<Condition>()
const newValue = ref<FilterItemValue>()
const filterApplied = ref(false)
const addingSimilarFilter = ref(false)

const tippyShown = computed(() => tippyRef.value?.tip.state.isVisible)

watch(
  () => [props.value, addingSimilarFilter.value],
  () => (newValue.value = addingSimilarFilter.value ? [] : cloneDeep(props.value)),
  { immediate: true },
)

watch(
  () => [props.selectedCondition, props.conditions, addingSimilarFilter.value],
  () => {
    if (!Object.keys(props.selectedCondition).length || addingSimilarFilter.value) {
      newSelectedCondition.value = props.conditions?.[0]
    } else newSelectedCondition.value = props.selectedCondition
  },
  { immediate: true },
)

const activeFilter = computed(() => isActiveFilter(props.value, props.selectedCondition))

// Count only the original groups from props, excluding dynamically added groups (like "All" or selected items)
// This ensures select-all remains visible even when items are selected and sorted to top
const hasMultipleGroups = computed(() => {
  const originalGroups = props.valueComponentProps?.groups || []
  // Filter out any auto-generated groups (they typically have special IDs or have `name: undefined` or `name: null`).
  // Note: Some original groups can have `name: ''` (empty string), so we use != null which excludes both null and undefined, but includes empty strings.
  const userDefinedGroups = originalGroups.filter((group) => group.name != null)
  return userDefinedGroups.length > 1
})

const applyingDisabled = computed(() => {
  if (props.skipErrorCheck) return false

  return newSelectedCondition.value?.valueRequired ? !valuesFilled(newValue.value) : !newSelectedCondition.value.label
})

const multipleValuesAllowed = computed(() =>
  Boolean(
    newSelectedCondition.value?.name?.startsWith('LONG_LIST') ||
      newSelectedCondition.value?.name?.startsWith('STRING_LIST') ||
      newSelectedCondition.value?.name?.includes('BREACHED'),
  ),
)

const getEmojiSrc = (value: DropdownSelectedValue[]) => {
  if (props.valueType !== FilterValueType.Generic) return
  if (value.length !== 1) return

  const selectedOption = (props.valueComponentProps?.groups ?? [])
    .map((g) => g.options)
    .flat()
    .find((o) => o.value === value[0].value)
  return selectedOption?.emojiSrc
}

const emojiSrc = computed(() => getEmojiSrc(props.value as DropdownSelectedValue[]))

const popoverHide = () => {
  addingSimilarFilter.value = false

  if (!filterApplied.value && !activeFilter.value) emit('remove')
  filterApplied.value = false
}

const hidePopover = () => {
  tippyRef.value?.tip.hide()
}

const applyClicked = ref(false)

const applyFilter = () => {
  applyClicked.value = true
  if (applyingDisabled.value) return

  if (addingSimilarFilter.value) {
    emit('add-similar-filter', {
      selectedCondition: newSelectedCondition.value,
      value: newValue.value,
    })
    addingSimilarFilter.value = false
  } else {
    emit('filter-applied', {
      selectedCondition: newSelectedCondition.value,
      value: newValue.value,
    })
  }

  filterApplied.value = true
  tippyRef.value?.tip.hide()
}

const getFieldLabel = (values: DropdownSelectedValue[]) => {
  if (!values.length) return null
  if (values.length === 1) return values[0].label

  return i18n.t('universal.items_selected', values.length)
}

const validateNumber = (value: number) => {
  if (value < 0) return (newValue.value = '')
  newValue.value = `${value}`
}

onClickOutside(filterItemPopup, (e: Event) => {
  if (e.target instanceof Element && !e.target.closest('[data-tippy-root]')) hidePopover()
})
</script>
